// Shared workout-builder option constants, types, and helpers.
// Used by both the plan builder (/plan/create) and the plan editor
// (/plan/:nanoid/edit) so their session cards stay identical.

export interface WorkoutDay {
  day: string;
  name: string;
  modality: string;
  notes: string;
  strength_focus: string;
  equipment: string;
  time: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration_minutes: number;
  other_notes: string;
}

export const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// day_number (1=Mon … 7=Sun) ↔ full day name
export const DAY_NUMBER_TO_NAME = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const dayNameToNumber = (name: string): number => Math.max(0, DAY_NUMBER_TO_NAME.indexOf(name));

export const TIME_OPTIONS: { label: string; value: string }[] = (() => {
  const opts: { label: string; value: string }[] = [{ label: 'Any time', value: '' }];
  for (let h = 5; h <= 22; h++) {
    for (const m of [0, 30]) {
      if (h === 22 && m === 30) continue;
      const hh = String(h).padStart(2, '0');
      const mm = m === 0 ? '00' : '30';
      const ampm = h < 12 ? 'AM' : 'PM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      opts.push({ label: `${h12}:${mm} ${ampm}`, value: `${hh}:${mm}` });
    }
  }
  return opts;
})();

export const MODALITY_OPTIONS = [
  { label: 'Running',      value: 'running',     defaultName: 'Run',                hasNotes: false },
  { label: 'Walking',      value: 'walking',     defaultName: 'Walk',               hasNotes: false },
  { label: 'Strength',     value: 'strength',    defaultName: 'Strength Training',  hasNotes: false },
  { label: 'HIIT',         value: 'hiit',        defaultName: 'HIIT Workout',       hasNotes: false },
  { label: 'Yoga',         value: 'yoga',        defaultName: 'Yoga Session',       hasNotes: false },
  { label: 'Mobility',     value: 'mobility',    defaultName: 'Mobility Work',      hasNotes: false },
  { label: 'Core',         value: 'core',        defaultName: 'Core Session',       hasNotes: false },
  { label: 'Stretch',      value: 'stretch',     defaultName: 'Stretch',            hasNotes: false },
  { label: 'Group Class',  value: 'group_class', defaultName: 'Group Class',        hasNotes: true  },
  { label: 'Sport',        value: 'sport',       defaultName: 'Sport',              hasNotes: true  },
  { label: 'Other Cardio', value: 'cardio',      defaultName: 'Cardio Session',     hasNotes: true  },
  { label: 'Other',        value: 'other',       defaultName: 'Workout',            hasNotes: true  },
];

export const DEFAULT_NAMES     = Object.fromEntries(MODALITY_OPTIONS.map(o => [o.value, o.defaultName]));
export const ALL_DEFAULT_NAMES = new Set(MODALITY_OPTIONS.map(o => o.defaultName));
export const NOTES_MODALITIES  = new Set(MODALITY_OPTIONS.filter(o => o.hasNotes).map(o => o.value));

export const STRENGTH_FOCUS = [
  { label: 'Upper Body', value: 'upper_body' },
  { label: 'Lower Body', value: 'lower_body' },
  { label: 'Core',       value: 'core'       },
  { label: 'Full Body',  value: 'full_body'  },
];

export const EQUIPMENT = [
  { label: 'Bodyweight',   value: 'bodyweight'   },
  { label: 'Free Weights', value: 'free_weights' },
  { label: 'Full Gym',     value: 'full_gym'     },
];

export const DIFFICULTY_OPTIONS: { label: string; value: WorkoutDay['difficulty'] }[] = [
  { label: 'Beginner',     value: 'beginner'     },
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Advanced',     value: 'advanced'     },
];

export const DURATION_OPTIONS = [15, 20, 30, 45, 60, 90];

export const NOTES_LABEL: Record<string, string> = {
  group_class: 'Class name', sport: 'Sport', cardio: 'Activity', other: 'Activity name',
};
export const NOTES_PLACEHOLDER: Record<string, string> = {
  group_class: 'e.g. Pilates, Spin, Barre...',
  sport:       'e.g. Tennis, Basketball, Soccer...',
  cardio:      'e.g. Cycling, Rowing, Elliptical...',
  other:       'e.g. Rock climbing, Dance, Martial arts...',
};

export const blankSession = (): WorkoutDay => ({
  day: '', name: '', modality: '', notes: '', strength_focus: '',
  equipment: '', time: '', difficulty: 'beginner', duration_minutes: 45, other_notes: '',
});

// Returns the modality-aware update to apply when a session's type changes:
// resets type-specific fields and swaps in the default name if the user hadn't
// set a custom one.
export function applyModalityChange(session: WorkoutDay, mod: string): WorkoutDay {
  const nameIsDefault = ALL_DEFAULT_NAMES.has(session.name) || session.name === '';
  return {
    ...session,
    modality:       mod,
    name:           nameIsDefault ? (DEFAULT_NAMES[mod] ?? 'Workout') : session.name,
    notes:          '',
    strength_focus: mod === 'strength' ? session.strength_focus : '',
    equipment:      mod === 'strength' ? session.equipment : '',
  };
}
